import Link from 'next/link';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import {
  ConsentPurposeDetail,
  ConsentPurposeType,
  PDPAInfoPayload,
} from './PDPAConsent.model';
import { useLocale } from '@components/Contexts/LocaleContext';

interface Props {
  choices: ConsentPurposeType[];
  payload: PDPAInfoPayload;
  setPayload: Dispatch<SetStateAction<PDPAInfoPayload>>;
  valid: boolean;
  setValid: Dispatch<SetStateAction<boolean>>;
  mode?: 'light' | 'dark';
  newDesign?: boolean;
}

const PDPAConsent: FC<Props> = ({
  choices,
  payload,
  setPayload,
  valid,
  setValid,
  mode = 'light',
  newDesign = false,
}) => {
  const [uiCheckbox, setUICheckbox] = useState<{
    [k in ConsentPurposeType]: boolean;
  }>({
    'PP-1': false,
    'PP-2': false,
    'PP-3': false,
    'PP-4': false,
    'PP-5': false,
  });
  const [showSendBy, setShowSendBy] = useState<boolean>(false);
  const [consent, setConsent] = useState<boolean>(false);
  const { lang } = useLocale();

  useEffect(() => {
    // update uiCheck when payload updated
    const purposes = payload?.purposes;
    if (!purposes) return;

    let updated = {};
    purposes.forEach((p) => {
      updated[p.purpose] = p.consented;
    });
    setUICheckbox({ ...uiCheckbox, ...updated });

    // validate form
    const allFalse = purposes.every((p) => !p.consented);
    if (allFalse) {
      setValid(false);
      return;
    }

    const selectSomeSendBy = purposes
      .filter((p) => ['PP-4', 'PP-5'].includes(p.purpose))
      .some((p) => p.consented);
    setValid(selectSomeSendBy);
  }, [payload]);

  const toggleCheckbox = (checkedType: ConsentPurposeType, allConsent?: boolean,) => {
    let { purposes } = payload;
    purposes = purposes.map((p) => {
      if (p.purpose !== checkedType) return p;

      if (allConsent !== undefined) {
        p.consented = allConsent;
      } else {
        p.consented = !p.consented;
      }
      return p;
    });
    setPayload({ ...payload, purposes });
  };

  const handleSelectChoice = (checkedType: ConsentPurposeType) => {
    toggleCheckbox(checkedType);

    const isCheckedSomeChoices = (payload?.purposes ?? [])
      .filter((p) => choices.includes(p.purpose))
      .some((p) => p.consented);
    if (isCheckedSomeChoices) {
      autoSelectSendByEmail();
      return;
    }

    const noCheckedAnyChoices = (payload?.purposes ?? [])
      .filter((p) => choices.includes(p.purpose))
      .every((p) => !p.consented);
    if (noCheckedAnyChoices) {
      clearAllCheck();
    }
  };

  const handleSelectConsent = (checked: boolean) => {
    setConsent(checked);
    payload.purposes.forEach((p) => {
      toggleCheckbox(p.purpose, checked);
    });
  };

  const handleSelectSendBy = (checkedType: ConsentPurposeType) => {
    toggleCheckbox(checkedType);
  };

  const autoSelectSendByEmail = () => {
    let { purposes } = payload;
    const alreadySelectSomeSendBy = purposes
      .filter((p) => ['PP-4', 'PP-5'].includes(p.purpose))
      .some((p) => p.consented);
    if (alreadySelectSomeSendBy) {
      return;
    }

    purposes = purposes.map((p) => {
      if (p.purpose !== 'PP-4') return p;

      p.consented = true;
      return p;
    });
    setPayload({ ...payload, purposes });
    setShowSendBy(true);
  };

  const clearAllCheck = () => {
    let { purposes } = payload;
    purposes = purposes.map((p) => {
      p.consented = false;
      return p;
    });
    setPayload({ ...payload, purposes });
    setShowSendBy(false);
  };

  if (newDesign) {
    return (
      <ul>
        <li className="flex mb-4">
          <div className="w-4 h-4">
            <input
              id="pdpa-consent"
              type="checkbox"
              checked={consent}
              className="cursor-pointer"
              onChange={(e) => handleSelectConsent(e.target.checked)}
            />
          </div>
          <div className="ml-2">
            <label htmlFor="pdpa-consent" className="cursor-pointer">
              {lang === 'en'
                ? 'I agree to be contacted by CODIUM regarding relevant products or services. See our'
                : 'ข้าพเจ้ายินยอมให้โคเดียมติดต่อและนำเสนอผลิตภัณฑ์หรือบริการที่เหมาะสม โดยสามารถอ่านเพิ่มเติมได้ที่'}
            </label>{' '}
            <Link href={'/privacy-policy'} passHref>
              <a
                target="_blank"
                className=" text-lightBlue underline cursor-pointer hover:text-lightBlue-600"
              >
                {lang === 'en' ? 'Privacy Policy.' : 'นโยบายความเป็นส่วนตัว'}
              </a>
            </Link>
          </div>
        </li>
      </ul>
    );
  }

  return (
    <>
      <p>
        <b>โคเดียมสัญญาว่าจะให้ความคุ้มครองข้อมูลส่วนบุคคลของคุณ</b>{' '}
        และเราจะใช้ข้อมูลส่วนบุคคลของคุณเท่าที่จำเป็นสำหรับการนำเสนอผลิตภัณฑ์/บริการที่คุณร้องขอมาเท่านั้น
        <br />
        <br />
        ในบางกรณีเราอาจจะติดต่อไปหาคุณเพื่อการนำเสนอผลิตภัณฑ์, บริการ
        หรือกิจกรรมที่ตรงกับความต้องการของคุณ
        ถ้าหากคุณต้องการให้เราติดต่อไปเพื่อวัตถุประสงค์ดังกล่าว
        โปรดเลือกให้ความยินยอมกับเราในกรณีต่อไปนี้
      </p>
      <br />

      <ul>
        {choices.map((choice) => (
          <li key={choice} className="py-2 flex items-center">
            <input
              id={choice}
              type="checkbox"
              checked={uiCheckbox[choice]}
              onChange={() =>
                handleSelectChoice(consentPurposeDetail[choice].value)
              }
              className="cursor-pointer"
            />
            <label htmlFor={choice} className="ml-4 cursor-pointer">
              {consentPurposeDetail[choice].label}
            </label>
          </li>
        ))}
      </ul>

      <br />

      {showSendBy ? (
        <>
          <p>โปรดเลือกช่องทางที่คุณต้องการให้เราติดต่อคุณ</p>
          <br />

          <ul className="flex space-x-28">
            <li className="flex items-center">
              <input
                id="byEmail"
                type="checkbox"
                checked={uiCheckbox['PP-4']}
                onChange={() => handleSelectSendBy('PP-4')}
                className="cursor-pointer"
              />
              <label htmlFor="byEmail" className="ml-4 cursor-pointer">
                {consentPurposeDetail['PP-4'].label}
              </label>
            </li>
            <li className="flex items-center">
              <input
                id="byPhone"
                type="checkbox"
                checked={uiCheckbox['PP-5']}
                onChange={() => handleSelectSendBy('PP-5')}
                className="cursor-pointer"
              />
              <label htmlFor="byPhone" className="ml-4 cursor-pointer">
                {consentPurposeDetail['PP-5'].label}
              </label>
            </li>
          </ul>

          {!valid ? (
            <p className="mt-2 text-red font-medium">
              กรุณาเลือกช่องทางการติดต่อ
            </p>
          ) : null}
        </>
      ) : null}

      <br />

      <p className={`${mode === 'dark' ? 'text-white' : 'text-neutral-600'}`}>
        * คุณสามารถถอนความยินยอมของคุณได้ทุกเมื่อ
        สำหรับช่องทางการติดต่อเพื่อขอถอนความยินยอม
        และรายละเอียดการคุ้มครองข้อมูล ส่วนบุคคลของคุณ โปรดอ่านได้ที่{' '}
        <Link href={'/privacy-policy'} passHref>
          <a
            target="_blank"
            className=" text-lightBlue underline cursor-pointer hover:text-lightBlue-600"
          >
            ประกาศความเป็นส่วนตัวของเรา
          </a>
        </Link>
      </p>
    </>
  );
};

export default PDPAConsent;

const consentPurposeDetail: {
  [key in ConsentPurposeType]: ConsentPurposeDetail;
} = {
  'PP-1': {
    label: 'เพื่อการวิเคราะห์และนำเสนอผลิตภัณฑ์บริการที่ฉันอาจสนใจ',
    value: 'PP-1',
  },
  'PP-2': {
    label: 'เพื่อการเชิญฉันเข้าร่วมกิจกรรม/งานสัมมนาที่ฉันอาจสนใจ',
    value: 'PP-2',
  },
  'PP-3': {
    label: 'เพื่อรับข่าวสารความรู้ใหม่ๆ ที่น่าสนใจจากโคเดียม',
    value: 'PP-3',
  },
  'PP-4': {
    label: 'อีเมล',
    value: 'PP-4',
  },
  'PP-5': {
    label: 'โทรศัพท์',
    value: 'PP-5',
  },
};
